import React, { useEffect, useState } from 'react';

import { RBAC_ROLES } from 'components/admin/utils/feature-flags';
import Loading from 'components/invisible/Loading';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import useAuth, { parseJwt } from 'hooks/useAuth';

interface Props {
  children: React.ReactNode;
}

interface SessionProps extends Props {
  guid: string;
  shopId: number;
}

const getIdentity = (token: string | null) => {
  if (!token) return undefined;
  try {
    return parseJwt(token).identity;
  } catch {
    return undefined;
  }
};

// Oak configures JWT_IDENTITY_CLAIM="identity" in postscript-api/run.py.
// TokenRefresh serializes guid, shop_id and string permission names into it.
// The issuer also copies shop_id to the top level for getShopId(); readiness
// validates the nested identity so permissions belong to this user and shop.
const hasExpectedClaims = (
  token: string | null,
  guid: string,
  shopId: number,
) => {
  const identity = getIdentity(token);
  return (
    identity?.guid === guid &&
    identity?.shop_id === shopId &&
    Array.isArray(identity?.permissions) &&
    identity.permissions.every(
      (permission: unknown) => typeof permission === 'string',
    )
  );
};

const SynchronizedPermissions = ({ children, guid, shopId }: SessionProps) => {
  const { accessToken, refreshAccessToken, clearAccessToken } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [hasMatchingContext, setHasMatchingContext] = useState(false);
  const claimsMatch = hasExpectedClaims(accessToken, guid, shopId);
  const identity = getIdentity(accessToken);
  const identityMatches =
    identity?.guid === guid && identity?.shop_id === shopId;

  useEffect(() => {
    if (identityMatches) setHasMatchingContext(true);
  }, [identityMatches]);

  useEffect(() => {
    if (!hasMatchingContext) return undefined;
    let cancelled = false;

    (async () => {
      // A flag/login transition needs an actual refresh, even during cooldown.
      // Concurrent callers still share the same request.
      const token = await refreshAccessToken({ force: true });
      if (cancelled || !token) return;

      if (!hasExpectedClaims(token, guid, shopId)) {
        clearAccessToken();
        return;
      }
      setIsReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [hasMatchingContext, refreshAccessToken, clearAccessToken, guid, shopId]);

  useEffect(() => {
    if (isReady && !claimsMatch) clearAccessToken();
  }, [isReady, claimsMatch, clearAccessToken]);

  return isReady && claimsMatch ? <>{children}</> : <Loading />;
};

export default function PermissionReadiness({ children }: Props): JSX.Element {
  const { user } = useUser();
  const { sessionVersion } = useAuth();
  const { hasInitialized, hasLabsFlag } = usePSLabs();

  if (!hasInitialized) return <Loading />;
  if (!user?.guid || !user.shop_id) return <Loading />;
  if (!hasLabsFlag(RBAC_ROLES)) return <>{children}</>;

  // Remount on login/shop changes. Turning RBAC off unmounts this boundary,
  // so every OFF-to-ON transition starts with unverified permissions.
  return (
    <SynchronizedPermissions
      key={`${sessionVersion}:${user.guid}:${user.shop_id}`}
      guid={user.guid}
      shopId={user.shop_id}
    >
      {children}
    </SynchronizedPermissions>
  );
}
